import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RTS_CONTACT = {
  company: "Riley Technology Solutions (RTS)",
  email: "johnriley1.rileytechsolutions@gmail.com",
  phone: "813.720.9057",
  website: "www.rileyts.com",
  businessType: "Veteran Owned Small Business (VOSB)",
  gsaContract: "GSA IT70 #GS35F0080V"
};

// Enhanced Live Chat Component
const LiveChat = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).substr(2, 9));
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    preferredDate: '',
    preferredTime: '',
    service: '',
    message: ''
  });
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message when chat opens
      setMessages([{
        type: 'ai',
        content: 'Hello! Welcome to Riley Technology Solutions. I\'m here to help you with information about our IT services, answer questions, or schedule a consultation. How can I assist you today?',
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  // FAQ Categories and Questions
  const faqCategories = {
    "General Info": [
      "What services does RTS offer?",
      "How long has RTS been in business?",
      "What is RTS's GSA contract number?",
      "Is RTS a veteran-owned business?"
    ],
    "IT Services": [
      "What is Managed Service Provider (MSP)?",
      "Do you provide 24/7 support?",
      "What cybersecurity services do you offer?",
      "Do you offer cloud migration services?"
    ],
    "Government Contracts": [
      "Do you work with federal agencies?",
      "What security clearances do you have?",
      "How do I use your GSA contract?",
      "What NAICS codes do you support?"
    ],
    "Getting Started": [
      "How do I schedule a consultation?",
      "What information do you need for a quote?",
      "How quickly can you respond to issues?",
      "Do you provide emergency support?"
    ]
  };

  const quickActions = [
    {
      icon: "📅",
      text: "Schedule Consultation",
      action: () => setShowConsultationForm(true)
    },
    {
      icon: "❓",
      text: "View FAQs",
      action: () => setShowQuickActions(!showQuickActions)
    },
    {
      icon: "📞",
      text: "Call Now",
      action: () => window.open(`tel:${RTS_CONTACT.phone}`, '_self')
    },
    {
      icon: "✉️",
      text: "Email Us",
      action: () => window.open(`mailto:${RTS_CONTACT.email}`, '_self')
    }
  ];

  const sendMessage = async (message) => {
    if (!message.trim()) return;

    setIsLoading(true);
    setShowQuickActions(false);

    // Add user message to chat
    setMessages(prev => [...prev, { 
      type: 'user', 
      content: message,
      timestamp: new Date()
    }]);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: message,
        session_id: sessionId
      });

      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: response.data.ai_response,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: 'I apologize, but I\'m experiencing technical difficulties. Please contact us directly at ' + RTS_CONTACT.phone + ' or ' + RTS_CONTACT.email,
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;
    
    const message = inputMessage.trim();
    setInputMessage("");
    sendMessage(message);
  };

  const handleFAQClick = (question) => {
    sendMessage(question);
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Consultation form submitted:', consultationForm);
    
    try {
      // Submit consultation request
      const response = await axios.post(`${API}/consultation-request`, consultationForm);
      console.log('Consultation response:', response.data);
      
      // Add confirmation message to chat
      setMessages(prev => [...prev, {
        type: 'ai',
        content: `Thank you ${consultationForm.name}! Your consultation request has been submitted successfully. We'll contact you at ${consultationForm.email}${consultationForm.phone ? ` or ${consultationForm.phone}` : ''} to confirm your appointment${consultationForm.preferredDate ? ` for ${consultationForm.preferredDate}` : ''}. Our team will reach out within 24 hours to finalize the details.`,
        timestamp: new Date()
      }]);
      
      console.log('Success message added to chat');
      
      // Reset form
      setConsultationForm({
        name: '', email: '', phone: '', company: '', 
        preferredDate: '', preferredTime: '', service: '', message: ''
      });
      setShowConsultationForm(false);
      
      console.log('Form reset and closed');
      
    } catch (error) {
      console.error('Consultation submission error:', error);
      setMessages(prev => [...prev, {
        type: 'ai',
        content: 'There was an error submitting your consultation request. Please try again or contact us directly at ' + RTS_CONTACT.phone,
        timestamp: new Date()
      }]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm sm:max-w-md h-[500px] sm:h-[600px] flex flex-col"
         style={{backgroundColor: 'white', borderRadius: '8px', height: 'min(600px, calc(100vh - 100px))'}}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white p-3 sm:p-4 rounded-t-lg flex justify-between items-center">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full mr-3 animate-pulse"></div>
            <div>
              <h3 className="text-lg font-semibold">RTS Live Chat</h3>
              <p className="text-xs opacity-90">We're online and ready to help!</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition duration-200">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {messages.map((message, index) => (
            <div key={index} className={`mb-4 ${message.type === 'user' ? 'text-right' : 'text-left'}`}>
              <div className={`inline-block p-3 rounded-lg max-w-[85%] ${
                message.type === 'user' 
                  ? 'bg-blue-800 text-white rounded-br-sm' 
                  : 'bg-white text-gray-800 rounded-bl-sm shadow-md border'
              }`}>
                <p className="text-sm">{message.content}</p>
                {message.timestamp && (
                  <p className={`text-xs mt-1 ${
                    message.type === 'user' ? 'text-blue-200' : 'text-gray-500'
                  }`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="text-left mb-4">
              <div className="inline-block p-3 rounded-lg bg-white text-gray-800 shadow-md border rounded-bl-sm">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          {showQuickActions && messages.length === 1 && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">Quick actions to get you started:</p>
              <div className="grid grid-cols-2 gap-2">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={action.action}
                    className="flex items-center p-2 bg-white border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition duration-200 text-sm"
                  >
                    <span className="mr-2">{action.icon}</span>
                    {action.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {!showQuickActions && !showConsultationForm && messages.length <= 3 && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">Frequently Asked Questions:</p>
              {Object.entries(faqCategories).map(([category, questions]) => (
                <div key={category} className="mb-3">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">{category}</h4>
                  <div className="space-y-1">
                    {questions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => handleFAQClick(question)}
                        className="block w-full text-left text-xs p-2 bg-white border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 transition duration-200"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Consultation Form Modal */}
        {showConsultationForm && (
          <div className="absolute inset-0 bg-white rounded-lg z-10 overflow-y-auto">
            <div className="bg-blue-800 text-white p-4 rounded-t-lg flex justify-between items-center">
              <h3 className="text-lg font-semibold">Schedule Consultation</h3>
              <button onClick={() => setShowConsultationForm(false)} className="text-white hover:text-gray-200">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleConsultationSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={consultationForm.name}
                    onChange={(e) => setConsultationForm({...consultationForm, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={consultationForm.company}
                    onChange={(e) => setConsultationForm({...consultationForm, company: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={consultationForm.email}
                  onChange={(e) => setConsultationForm({...consultationForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={consultationForm.phone}
                  onChange={(e) => setConsultationForm({...consultationForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date</label>
                  <input
                    type="date"
                    value={consultationForm.preferredDate}
                    onChange={(e) => setConsultationForm({...consultationForm, preferredDate: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Time</label>
                  <select
                    value={consultationForm.preferredTime}
                    onChange={(e) => setConsultationForm({...consultationForm, preferredTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select time</option>
                    <option value="9:00 AM">9:00 AM</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="1:00 PM">1:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Interest</label>
                <select
                  value={consultationForm.service}
                  onChange={(e) => setConsultationForm({...consultationForm, service: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select service</option>
                  <option value="MSP">Managed Service Provider (MSP)</option>
                  <option value="Cybersecurity">Cybersecurity Services</option>
                  <option value="Cloud">Cloud Innovation & Strategy</option>
                  <option value="Federal">Federal Government Services</option>
                  <option value="Infrastructure">Infrastructure Solutions</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={consultationForm.message}
                  onChange={(e) => setConsultationForm({...consultationForm, message: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tell us about your IT needs..."
                ></textarea>
              </div>
              
              <button
                type="submit"
                className="w-full bg-blue-800 text-white py-3 px-4 rounded-md font-semibold hover:bg-blue-900 transition duration-300"
              >
                Schedule Consultation
              </button>
            </form>
          </div>
        )}

        {/* Input Area */}
        {!showConsultationForm && (
          <form onSubmit={handleSubmit} className="p-4 border-t bg-white rounded-b-lg">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading || !inputMessage.trim()}
                className="bg-blue-800 text-white px-4 py-2 rounded-full hover:bg-blue-900 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Press Enter to send • We typically respond within minutes
            </p>
          </form>
        )}
    </div>
  );
};

// Floating Chat Widget
const FloatingChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show widget after 3 seconds
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
    }
  }, [isOpen]);

  const handleChatOpen = () => {
    console.log('Chat button clicked, current state:', isOpen);
    setIsOpen(true);
  };

  const handleChatClose = () => {
    console.log('Chat close requested');
    setIsOpen(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <div className="fixed bottom-20 right-4" style={{zIndex: 9999}}>
          <button
            onClick={handleChatOpen}
            className="bg-gradient-to-r from-blue-800 to-blue-900 text-white w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-2xl hover:shadow-3xl hover:scale-105 transition duration-300 flex items-center justify-center group"
            style={{zIndex: 9999}}
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            
            {hasUnread && (
              <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              </div>
            )}
          </button>
          
          {/* Chat Tooltip */}
          <div className="absolute bottom-16 right-0 mb-2 bg-black text-white text-xs py-2 px-3 rounded-lg opacity-0 group-hover:opacity-100 transition duration-300 whitespace-nowrap">
            Need help? Chat with us!
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
          </div>
        </div>
      )}

      {/* Live Chat Component - Force render when open */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 50,
          display: 'flex',
          alignItems: 'end',
          justifyContent: 'center',
          padding: '0.5rem'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '28rem',
            margin: '0 auto'
          }}>
            <LiveChat isOpen={isOpen} onClose={handleChatClose} />
          </div>
        </div>
      )}
    </>
  );
};

export { LiveChat, FloatingChatWidget };
export default FloatingChatWidget;